import React, { useState, useEffect } from 'react'
import {
  Box,
  Typography,
  Paper,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  FormControlLabel,
  Checkbox,
  Switch,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Snackbar,
  Card,
  CardContent,
  Divider,
} from '@mui/material'
import {
  Sync as SyncIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  CloudDownload as CloudDownloadIcon,
  Refresh as RefreshIcon,
  Layers as LayersIcon,
  ListAlt as ListAltIcon,
  Storage as StorageIcon,
} from '@mui/icons-material'
import { useQuery, useMutation } from '@tanstack/react-query'
import { empresasApi } from '../../api/empresas'
import { adminSyncApi, CopiarPadroesResponse } from '../../api/adminSync'

const ENTIDADES_DISPONIVEIS = [
  { key: 'telas', label: 'Telas do Sistema', desc: 'Cadastro mestre de telas, rotas e hierarquia de menu' },
  { key: 'telas_campos', label: 'Campos de Telas', desc: 'Regras de visibilidade e editabilidade por campo' },
  { key: 'tipos_aprovacao', label: 'Tipos de Aprovação', desc: 'Regras e critérios de aprovação bloqueadora/fluxo' },
  { key: 'fluxos_trabalho', label: 'Fluxos de Trabalho', desc: 'Definição de workflows (BPA) multi-setor' },
  { key: 'fluxos_etapas', label: 'Etapas de Fluxo', desc: 'Sequência de passos, telas e aprovadores por fluxo' },
]

export const CopiarPadroesTenantPage: React.FC = () => {
  const [selectedEmpresaId, setSelectedEmpresaId] = useState<number | ''>('')
  const [entidadesSelecionadas, setEntidadesSelecionadas] = useState<string[]>(
    ENTIDADES_DISPONIVEIS.map((e) => e.key)
  )
  const [modoSobrescrever, setModoSobrescrever] = useState<boolean>(true)
  const [resultadoUltimaCopia, setResultadoUltimaCopia] = useState<CopiarPadroesResponse | null>(null)

  const [snackbar, setSnackbar] = useState<{
    open: boolean
    message: string
    severity: 'success' | 'error' | 'info'
  }>({
    open: false,
    message: '',
    severity: 'info',
  })

  // 1. Buscar empresas ativas
  const { data: empresas = [] } = useQuery({
    queryKey: ['empresas-sync-padroes'],
    queryFn: () => empresasApi.list(undefined, { includeInactive: false }),
  })

  // Selecionar primeira empresa por padrão se disponível
  useEffect(() => {
    if (empresas.length > 0 && selectedEmpresaId === '') {
      setSelectedEmpresaId(empresas[0].id)
    }
  }, [empresas, selectedEmpresaId])

  // 2. Query de status de sincronização da empresa selecionada
  const {
    data: syncStatus,
    isLoading: isLoadingStatus,
    refetch: refetchStatus,
  } = useQuery({
    queryKey: ['admin-sync-status', selectedEmpresaId],
    queryFn: () => adminSyncApi.getSyncStatus(Number(selectedEmpresaId)),
    enabled: typeof selectedEmpresaId === 'number' && selectedEmpresaId > 0,
  })

  // 3. Mutation para disparar a cópia de padrões
  const syncMutation = useMutation({
    mutationFn: adminSyncApi.copiarPadroesTenant,
    onSuccess: (data) => {
      setResultadoUltimaCopia(data)
      setSnackbar({
        open: true,
        message: data.mensagem || 'Cópia de padrões concluída com sucesso!',
        severity: data.success ? 'success' : 'error',
      })
      refetchStatus()
    },
    onError: (error: any) => {
      setSnackbar({
        open: true,
        message: error?.response?.data?.detail || 'Erro ao copiar padrões para o tenant.',
        severity: 'error',
      })
    },
  })

  const handleToggleEntidade = (key: string) => {
    setEntidadesSelecionadas((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    )
  }

  const handleSelectAllEntidades = () => {
    if (entidadesSelecionadas.length === ENTIDADES_DISPONIVEIS.length) {
      setEntidadesSelecionadas([])
    } else {
      setEntidadesSelecionadas(ENTIDADES_DISPONIVEIS.map((e) => e.key))
    }
  }

  const handleExecutarCopia = () => {
    if (!selectedEmpresaId) {
      setSnackbar({
        open: true,
        message: 'Por favor, selecione uma empresa de destino.',
        severity: 'error',
      })
      return
    }
    if (entidadesSelecionadas.length === 0) {
      setSnackbar({
        open: true,
        message: 'Selecione ao menos uma entidade para copiar.',
        severity: 'error',
      })
      return
    }

    syncMutation.mutate({
      empresa_id: Number(selectedEmpresaId),
      entidades: entidadesSelecionadas,
      modo_sobrescrever: modoSobrescrever,
    })
  }

  const empresaSelecionadaObj = empresas.find((e) => e.id === selectedEmpresaId)

  return (
    <Box className="p-6 space-y-6 max-w-7xl mx-auto">
      {/* Cabeçalho */}
      <Paper className="p-6 rounded-2xl bg-gradient-to-r from-slate-900 via-slate-800 to-indigo-950 text-white shadow-xl border border-slate-700/60">
        <Box className="flex items-center justify-between">
          <Box className="flex items-center gap-4">
            <Box className="p-3 bg-indigo-600/30 rounded-xl border border-indigo-400/30">
              <CloudDownloadIcon className="w-8 h-8 text-indigo-300" />
            </Box>
            <Box>
              <Typography variant="h5" className="font-bold text-slate-100">
                Cópia de Padrões Admin (galatea_auth → Tenant)
              </Typography>
              <Typography variant="body2" className="text-slate-300">
                Sincronize cadastros modelo (Telas, Campos, Aprovadores e Fluxos BPA) para os bancos de dados relacionais dos tenants sob demanda.
              </Typography>
            </Box>
          </Box>

          <Button
            variant="outlined"
            onClick={() => refetchStatus()}
            disabled={isLoadingStatus || !selectedEmpresaId}
            startIcon={<RefreshIcon />}
            className="border-slate-500 text-slate-200 hover:bg-slate-800"
          >
            Atualizar Status
          </Button>
        </Box>
      </Paper>

      {/* Seleção do Tenant */}
      <Paper className="p-6 rounded-xl bg-slate-900/60 backdrop-blur-md border border-slate-700/50 shadow-md">
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={6}>
            <FormControl fullWidth size="small">
              <InputLabel id="select-empresa-label" className="text-slate-300">
                Empresa / Tenant de Destino
              </InputLabel>
              <Select
                labelId="select-empresa-label"
                value={selectedEmpresaId}
                label="Empresa / Tenant de Destino"
                onChange={(e) => setSelectedEmpresaId(e.target.value as number)}
                className="bg-slate-800 text-slate-100 rounded-lg"
              >
                {empresas.map((emp) => (
                  <MenuItem key={emp.id} value={emp.id}>
                    <span className="font-semibold text-indigo-400">[{emp.id}]</span> {emp.nome} ({emp.banco_dados || 'Sem Banco registrado'})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            {empresaSelecionadaObj && (
              <Box className="p-3 bg-slate-800/80 rounded-lg border border-slate-700 text-sm flex items-center justify-between text-slate-300">
                <Box className="flex items-center gap-2">
                  <StorageIcon className="w-4 h-4 text-indigo-400" />
                  <span>Banco de Dados: <strong>{empresaSelecionadaObj.banco_dados || 'N/A'}</strong></span>
                </Box>
                <Chip
                  label={empresaSelecionadaObj.status === 'A' ? 'Ativa' : 'Inativa'}
                  color={empresaSelecionadaObj.status === 'A' ? 'success' : 'default'}
                  size="small"
                />
              </Box>
            )}
          </Grid>
        </Grid>
      </Paper>

      {/* Tabela Comparativa de Status em Tempo Real */}
      <Paper className="p-6 rounded-xl bg-slate-900/60 backdrop-blur-md border border-slate-700/50 shadow-md">
        <Typography variant="h6" className="font-semibold text-slate-100 mb-4 flex items-center gap-2">
          <ListAltIcon className="text-indigo-400" /> Comparativo de Cadastros Padrão
        </Typography>

        {isLoadingStatus ? (
          <Box className="flex justify-center p-8">
            <CircularProgress color="primary" />
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow className="bg-slate-800/80">
                  <TableCell className="text-slate-300 font-semibold">Entidade / Tabela</TableCell>
                  <TableCell className="text-slate-300 font-semibold text-center">Registros em galatea_auth</TableCell>
                  <TableCell className="text-slate-300 font-semibold text-center">Registros no Tenant</TableCell>
                  <TableCell className="text-slate-300 font-semibold text-center">Status de Sincronização</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {syncStatus?.status_entidades.map((item) => {
                  const infoObj = ENTIDADES_DISPONIVEIS.find((e) => e.key === item.entidade)
                  return (
                    <TableRow key={item.entidade} className="hover:bg-slate-800/50">
                      <TableCell className="text-slate-200">
                        <span className="font-medium text-slate-100">{infoObj?.label || item.entidade}</span>
                        <br />
                        <span className="text-xs text-slate-400">{infoObj?.desc || item.entidade}</span>
                      </TableCell>
                      <TableCell className="text-slate-300 text-center font-semibold">{item.qtd_auth}</TableCell>
                      <TableCell className="text-slate-300 text-center font-semibold">{item.qtd_tenant}</TableCell>
                      <TableCell className="text-center">
                        {item.sincronizado ? (
                          <Chip
                            icon={<CheckCircleIcon />}
                            label="Sincronizado"
                            color="success"
                            size="small"
                            variant="outlined"
                          />
                        ) : (
                          <Chip
                            icon={<WarningIcon />}
                            label="Pendente de Cópia"
                            color="warning"
                            size="small"
                            variant="outlined"
                          />
                        )}
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Configuração de Cópia e Ação */}
      <Paper className="p-6 rounded-xl bg-slate-900/60 backdrop-blur-md border border-slate-700/50 shadow-md space-y-4">
        <Typography variant="h6" className="font-semibold text-slate-100 flex items-center gap-2">
          <LayersIcon className="text-indigo-400" /> Seleção de Entidades a Copiar
        </Typography>

        <Box className="flex items-center justify-between pb-2 border-b border-slate-700">
          <FormControlLabel
            control={
              <Checkbox
                checked={entidadesSelecionadas.length === ENTIDADES_DISPONIVEIS.length}
                onChange={handleSelectAllEntidades}
                color="primary"
              />
            }
            label={<span className="text-slate-200 font-semibold">Selecionar Todas as Tabelas Padrão</span>}
          />

          <FormControlLabel
            control={
              <Switch
                checked={modoSobrescrever}
                onChange={(e) => setModoSobrescrever(e.target.checked)}
                color="primary"
              />
            }
            label={
              <span className="text-slate-300 text-sm">
                Modo Sobrescrever / Upsert (atualiza registros existentes com base no ID)
              </span>
            }
          />
        </Box>

        <Grid container spacing={2}>
          {ENTIDADES_DISPONIVEIS.map((item) => (
            <Grid item xs={12} sm={6} md={4} key={item.key}>
              <Paper
                onClick={() => handleToggleEntidade(item.key)}
                className={`p-3 rounded-lg border cursor-pointer transition-all ${
                  entidadesSelecionadas.includes(item.key)
                    ? 'bg-indigo-950/40 border-indigo-500 text-slate-100 shadow-md'
                    : 'bg-slate-800/40 border-slate-700 text-slate-400 hover:border-slate-600'
                }`}
              >
                <Box className="flex items-start gap-2">
                  <Checkbox
                    checked={entidadesSelecionadas.includes(item.key)}
                    onChange={() => handleToggleEntidade(item.key)}
                    size="small"
                    color="primary"
                  />
                  <Box>
                    <Typography variant="subtitle2" className="font-semibold">
                      {item.label}
                    </Typography>
                    <Typography variant="caption" className="text-slate-400">
                      {item.desc}
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>

        <Box className="pt-4 flex justify-end">
          <Button
            variant="contained"
            color="primary"
            size="large"
            onClick={handleExecutarCopia}
            disabled={syncMutation.isPending || !selectedEmpresaId || entidadesSelecionadas.length === 0}
            startIcon={syncMutation.isPending ? <CircularProgress size={20} color="inherit" /> : <SyncIcon />}
            className="bg-indigo-600 hover:bg-indigo-700 font-semibold text-white px-8 py-3 rounded-xl shadow-lg"
          >
            {syncMutation.isPending ? 'Copiando Padrões...' : 'Copiar Padrões para Tenant'}
          </Button>
        </Box>
      </Paper>

      {/* Relatório Final da Última Execução */}
      {resultadoUltimaCopia && (
        <Paper className="p-6 rounded-xl bg-slate-900/80 backdrop-blur-md border border-indigo-500/40 shadow-xl space-y-4">
          <Typography variant="h6" className="font-bold text-indigo-300 flex items-center gap-2">
            <CheckCircleIcon className="text-emerald-400" /> Relatório da Execução de Cópia
          </Typography>
          <Typography variant="body2" className="text-slate-300">
            {resultadoUltimaCopia.mensagem}
          </Typography>

          <Grid container spacing={3}>
            {Object.entries(resultadoUltimaCopia.resultados).map(([key, res]) => {
              const label = ENTIDADES_DISPONIVEIS.find((e) => e.key === key)?.label || key
              return (
                <Grid item xs={12} sm={6} md={4} key={key}>
                  <Card className="bg-slate-800/90 border border-slate-700 rounded-xl">
                    <CardContent className="space-y-2">
                      <Typography variant="subtitle1" className="font-bold text-slate-100">
                        {label}
                      </Typography>
                      <Divider className="border-slate-700" />
                      <Box className="text-xs space-y-1 text-slate-300">
                        <div className="flex justify-between"><span>Registros lidos:</span> <strong>{res.lidos}</strong></div>
                        <div className="flex justify-between text-emerald-400"><span>Novos inseridos:</span> <strong>+{res.inseridos}</strong></div>
                        <div className="flex justify-between text-sky-400"><span>Atualizados:</span> <strong>{res.atualizados}</strong></div>
                        {res.erros.length > 0 && (
                          <div className="text-red-400 mt-2">
                            <strong>Erros ({res.erros.length}):</strong>
                            <ul className="list-disc pl-4 mt-1 space-y-1 max-h-24 overflow-y-auto">
                              {res.erros.map((err, idx) => (
                                <li key={idx}>{err}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              )
            })}
          </Grid>
        </Paper>
      )}

      {/* Snackbar feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  )
}

export default CopiarPadroesTenantPage
